import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { animate, stagger, createTimeline } from "animejs";
import { ArrowRight, Sparkles, Mail } from "lucide-react";
import { GithubIcon } from "../../components/ui/brand-icons";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { MENU_PATHS } from "../../utils/menu";

const ROLES = [
  "Frontend Developer",
  "Backend Developer",
  "Mobile Developer",
  "Fullstack Developer",
];

const TECH_STACK = [
  "React",
  "Next.js",
  "TypeScript",
  "Node.js",
  "Nest.js",
  "Flutter",
  "React Native",
  "AWS",
  "GCP",
  "Cloudflare",
  "MongoDB",
  "SQL",
];

const HomeIndex = () => {
  const navigate = useNavigate();
  const headlineRef = useRef(null);
  const heroRef = useRef(null);
  const portraitRef = useRef(null);
  const roleRef = useRef(null);

  // headline letter-by-letter reveal (anime.js v4)
  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;
    const text = el.dataset.text || "";
    el.innerHTML = text
      .split("")
      .map(
        (ch) =>
          `<span class="inline-block opacity-0 translate-y-6">${
            ch === " " ? "&nbsp;" : ch
          }</span>`
      )
      .join("");

    animate(el.querySelectorAll("span"), {
      opacity: [0, 1],
      translateY: [30, 0],
      filter: ["blur(8px)", "blur(0px)"],
      duration: 900,
      delay: stagger(35, { start: 250 }),
      ease: "outExpo",
    });
  }, []);

  // portrait float (gsap continuous)
  useEffect(() => {
    if (!portraitRef.current) return;
    const tween = gsap.to(portraitRef.current, {
      y: -12,
      duration: 3,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
    return () => tween.kill();
  }, []);

  // role text rotator with anime.js timeline
  useEffect(() => {
    const node = roleRef.current;
    if (!node) return;
    let i = 0;
    const tl = createTimeline({ loop: true });
    ROLES.forEach((role) => {
      tl.add(node, {
        opacity: [0, 1],
        translateY: [16, 0],
        duration: 600,
        ease: "outQuad",
        onBegin: () => {
          node.textContent = ROLES[i % ROLES.length];
          i++;
        },
      })
        .add(node, {
          opacity: [1, 0],
          translateY: [0, -16],
          duration: 600,
          ease: "inQuad",
          delay: 1800,
        });
    });
    return () => tl.pause();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative mx-auto flex min-h-screen max-w-7xl flex-col items-center justify-center px-6 py-24"
    >
      {/* status chip */}
      <div className="rise-in mb-8" style={{ animationDelay: "100ms" }}>
        <Badge variant="neon" className="gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
          </span>
          Available for new projects
        </Badge>
      </div>

      <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2">
        {/* LEFT — text */}
        <div className="text-center lg:text-left">
          <p
            className="rise-in mb-3 font-mono text-sm uppercase tracking-[0.4em] text-cyan-300/80"
            style={{ animationDelay: "200ms" }}
          >
            {"// welcome.tsx"}
          </p>

          <h1
            ref={headlineRef}
            data-text="Arnupap Sukserm"
            aria-label="Arnupap Sukserm"
            className="text-glow text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl"
          >
            Arnupap Sukserm
          </h1>

          <div
            className="rise-in mt-4 flex items-center justify-center gap-3 lg:justify-start"
            style={{ animationDelay: "1100ms" }}
          >
            <span className="font-mono text-base text-white/50">I'm a</span>
            <span
              ref={roleRef}
              className="text-neon text-xl font-bold sm:text-2xl"
            >
              Frontend Developer
            </span>
          </div>

          <p
            className="rise-in mt-6 max-w-xl text-base text-white/60 lg:text-lg"
            style={{ animationDelay: "1200ms" }}
          >
            Crafting performant, animated and accessible interfaces with{" "}
            <span className="text-cyan-300">5+ years</span> of professional
            experience across web, mobile and cloud.
          </p>

          {/* CTAs */}
          <div
            className="rise-in mt-10 flex flex-col items-center gap-3 sm:flex-row lg:items-start lg:justify-start"
            style={{ animationDelay: "1350ms" }}
          >
            <Button
              variant="neon"
              size="lg"
              onClick={() => navigate(MENU_PATHS.EXPERIENCE)}
              className="group"
            >
              <Sparkles className="h-4 w-4" />
              Explore Skills
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(MENU_PATHS.CONTACTS)}
            >
              <Mail className="h-4 w-4" />
              Contact Me
            </Button>
          </div>

          {/* tech marquee */}
          <div
            className="rise-in marquee-mask mt-12 overflow-hidden"
            style={{ animationDelay: "1500ms" }}
          >
            <div className="flex w-max gap-3 animate-infinite-scroll">
              {[...TECH_STACK, ...TECH_STACK].map((t, i) => (
                <span
                  key={i}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-xs text-white/70 backdrop-blur"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — portrait card */}
        <div
          className="rise-in flex items-center justify-center"
          style={{ animationDelay: "400ms" }}
        >
          <div ref={portraitRef} className="relative">
            {/* spinning conic ring */}
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-cyan-500/40 via-fuchsia-500/40 to-violet-500/40 opacity-60 blur-2xl" />
            <div className="border-conic relative overflow-hidden rounded-3xl">
              <Card className="relative overflow-hidden rounded-3xl border-0 p-0">
                <div className="relative h-[420px] w-[320px] sm:h-[460px] sm:w-[360px]">
                  <img
                    src="/images/image-profile.jpg"
                    alt="Arnupap Sukserm"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs uppercase tracking-widest text-cyan-300">
                          ID — 0xAS
                        </p>
                        <p className="text-lg font-semibold text-white">
                          Arnupap S.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href="mailto:arnupap.sukserm@gmail.com"
                          className="rounded-full border border-white/20 bg-white/10 p-2 backdrop-blur transition hover:bg-white/20"
                          aria-label="Email"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                        <a
                          href="https://github.com/comeonlnw"
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-white/20 bg-white/10 p-2 backdrop-blur transition hover:bg-white/20"
                          aria-label="GitHub"
                        >
                          <GithubIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            {/* stat chip */}
            <div className="glass absolute -left-6 top-8 hidden rounded-xl px-4 py-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                Years
              </p>
              <p className="text-neon text-2xl font-bold">5+</p>
            </div>
            <div className="glass absolute -right-6 bottom-24 hidden rounded-xl px-4 py-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                Stack
              </p>
              <p className="text-neon text-2xl font-bold">12+</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeIndex;
